import React, { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import { ErrorCard, LoadingCard } from 'src/commons/components/Card';
import {
  ApplicationStatus,
  ApplicationType,
  CARD_SERVICING_PATHS,
  CARDSTAR_OVERRIDE_ACCOUNT_CREATION,
  CreditCardApplicationAutopayPreferences,
  SelectedBankAccount,
  SET_PAYMENT_DATE_OPTIMIZELY_KEY,
} from 'src/commons/constants';
import { ifFeatureFlagOn } from 'src/commons/utilities/feature-flags';
import { heapTrackElementRendered } from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';

// Returns the AutoPay resume route based on app status & selectedBankAccount
const getAutoPayResumeRoute = (
  applicationId: string,
  applicationStatus: string,
  selectedBankAccount: SelectedBankAccount,
  autopayPreferences?: CreditCardApplicationAutopayPreferences
) => {
  const showSetPaymentDate = ifFeatureFlagOn(SET_PAYMENT_DATE_OPTIMIZELY_KEY);
  // Navigate to AutoPay Preferences page for One-Step users who have finished Plaid
  if (
    applicationStatus === ApplicationStatus.PENDING_UNCONDITIONAL_SUBMISSION &&
    selectedBankAccount &&
    !autopayPreferences
  ) {
    return prefixRoute(`/application/${applicationId}/auto-pay/preferences`);
  }
  // Navigate to the PaymentDate page if no monthlyPaymentDate has been saved...
  // ...and the npc-set-payment-date feature flag is enabled
  if (
    autopayPreferences &&
    autopayPreferences.paymentAmountType === 'NO_AUTOPAY' &&
    !autopayPreferences.monthlyPaymentDay &&
    showSetPaymentDate
  ) {
    // Track visibility of PaymentDate to monitor initial launch of this new feature
    heapTrackElementRendered('PaymentDatePage');
    return prefixRoute(`/application/${applicationId}/payment-date`);
  }
  // Navigate to the waiting room page for Two-Step users with a monthlyPaymentDate
  if (
    applicationStatus === ApplicationStatus.PENDING_SUBMISSION &&
    autopayPreferences
  ) {
    return prefixRoute(`/application/${applicationId}/export-waiting-room`);
  }
  // Navigate to AutoPay Setup page by default
  return prefixRoute(`/application/${applicationId}/auto-pay/setup`);
};

// Looks at the app status to determine a route to navigate to
export const getResumeRouteFromAppStatus = (application: ApplicationType) => {
  const {
    applicationId,
    applicationStatus,
    selectedBankAccount,
    autopayPreferences,
  } = application;
  const isCardStarRedirectOn = ifFeatureFlagOn(
    CARDSTAR_OVERRIDE_ACCOUNT_CREATION
  );
  switch (applicationStatus) {
    case 'OFFER_IN_PROGRESS':
    case 'OFFER_ERROR':
    case 'UNCONDITIONAL_VERIFICATION_IN_PROGRESS':
    case 'UNCONDITIONAL_VERIFICATION_ERROR':
    case 'PENDING_CONDITIONAL_DEVICE_VALIDATION':
    case 'PENDING_UNCONDITIONAL_DEVICE_VALIDATION':
    case 'PENDING_SSN_REENTRY':
      // Assign the offer in progress page
      return prefixRoute(`/application/${applicationId}/offer-waiting-room`);
    case 'PENDING_OFFER_ACCEPT':
    case 'PENDING_UNCONDITIONAL_OFFER_ACCEPT':
      // Assign the offer page
      return prefixRoute(`/application/${applicationId}/offer`);
    case 'PENDING_BANK_CONNECT_AUTHORIZATION':
    case 'PENDING_BANK_CONNECT':
      // Assign the bank connect page
      return prefixRoute(`/application/${applicationId}/connect-to-bank`);
    case 'VERIFICATION_IN_PROGRESS':
      // Assign the verification email page
      return prefixRoute(`/application/${applicationId}/verification-email`);
    case 'VERIFICATION_ERROR':
      // Assign the verification waiting room page
      return prefixRoute(
        `/application/${applicationId}/verification-waiting-room`
      );
    case 'PENDING_SUBMISSION':
    case 'PENDING_UNCONDITIONAL_SUBMISSION':
      // Assign the autopay page corresponding to app status & selectedBankAccount
      return getAutoPayResumeRoute(
        applicationId,
        applicationStatus,
        selectedBankAccount,
        autopayPreferences
      );
    case 'EXPORT_IN_PROGRESS':
    case 'EXPORT_ERROR':
      // Assign the export waiting room page
      return prefixRoute(`/application/${applicationId}/export-waiting-room`);
    case 'APPLICATION_COMPLETED':
      // Assign the you're-finished page
      if (isCardStarRedirectOn) {
        window.location.href = CARD_SERVICING_PATHS.dashboard;
        return null;
      }
      return prefixRoute(`/application/${applicationId}/finish-up`);
    case 'REJECTION_IN_PROGRESS':
    case 'REJECTION_ERROR':
    case 'APPLICATION_REJECTED':
      // Assign the decline page
      return prefixRoute(`/application/${applicationId}/decline`);
    case 'APPLICATION_WITHDRAWN':
      return prefixRoute('/');
    default:
      // Assign the registration page
      return prefixRoute('/');
  }
};

export const AppResume = () => {
  const navigate = useNavigate();

  const {
    creditCardApplication,
    fetchCreditCardApplication,
    showApplicationFetchError,
  } = useContext(CreditCardApplicationContext);

  useEffect(() => {
    fetchCreditCardApplication();
  }, []);

  useEffect(() => {
    // Check the status and redirect accordingly
    if (creditCardApplication && creditCardApplication.applicationStatus) {
      navigate(getResumeRouteFromAppStatus(creditCardApplication));
    }
  }, [creditCardApplication]);

  // If something went wrong show error indication
  if (showApplicationFetchError) {
    return <ErrorCard />;
  }

  // Otherwise just show the blinker while we fetch the application
  return <LoadingCard />;
};
